import { useEffect, useRef, useState } from "react";
import {
  scrollState,
  setPhase,
  onScrollChange,
  onPhaseChange,
  setMapProgress,
} from "../state/scroll";
import { useContent } from "../state/siteContent";

// Lazy-load maplibre-gl and its CSS only the first time the MAP phase is entered.
// Avoids loading ~450KB of JS on initial bundle — pays the cost only if the user
// actually reaches the map (nearly always, but keeps the initial JS smaller).
let maplibrePromise = null;
function loadMapLibre() {
  if (!maplibrePromise) {
    maplibrePromise = Promise.all([
      import("maplibre-gl"),
      import("maplibre-gl/dist/maplibre-gl.css"),
    ]).then(([mod]) => mod.default || mod);
  }
  return maplibrePromise;
}

const OFM_STYLE_URL = "https://tiles.openfreemap.org/styles/fiord";
const OFM_STYLE_LIGHT = "https://tiles.openfreemap.org/styles/bright";
const MAP_STYLE_KEY = "nr_map_style";

// The reveal pans from MAP_START_CENTER to OFFICE_CENTER while the map sits
// at a fixed pitch+bearing (tilted and rotated). On a wide/landscape
// viewport that pan reads as a gentle diagonal converging toward centre.
// On a narrow/portrait phone viewport the SAME lng/lat delta projects onto
// screen very differently once bearing rotates the view: it read as a
// hard sideways (left/right) shift for the first part of the scroll, only
// turning into the expected downward/converging motion later — because a
// non-zero bearing rotates which screen axis a pure longitude change
// lands on, and portrait's narrow width exaggerates that rotated component.
// Fix: drop bearing to 0 on narrow viewports so lng/lat panning maps back
// onto screen axes directly (still tilted via pitch, just not rotated) —
// keeps the cinematic tilt without the sideways-then-down zigzag.
function isNarrowViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 900;
}

// Office: Pimpri-Chinchwad, Pune — this is where the map zooms to
const OFFICE_CENTER = [73.7985, 18.6289];

// Start framing: Pune office + all 4 project sites clearly in view.
// The sites now span Shirdi (19.77°N) down to Dapoli (17.76°N), so the
// start pulls back slightly vs the old 3-site framing.
const MAP_START_CENTER = [73.55, 18.72];

// Narrow (portrait) start center: sits almost directly NORTH of the office
// (nearly the same longitude, higher latitude) so the reveal pan is
// primarily a vertical top→down descent onto the office rather than the
// mostly-eastward travel the landscape framing produces. The landscape
// MAP_START_CENTER has a longitude delta ~2.7x its latitude delta, so even
// with bearing 0 the camera genuinely moves mostly sideways then down —
// which on a phone read as "drifts left→right, THEN dives down". Keeping
// the sideways component near zero on portrait removes that entirely.
const MAP_START_CENTER_NARROW = [73.78, 19.15];

// Chosen per-viewport once at module load — see isNarrowViewport().
const START_CENTER = () =>
  isNarrowViewport() ? MAP_START_CENTER_NARROW : MAP_START_CENTER;

const ZOOM_START = 7.1;
const ZOOM_END   = 13.5;
const ZOOM_HANDOFF_THRESHOLD = 0.96;

// The map's own dark/bright style is intentionally independent of the
// site-wide day/night theme. Per the brief the map opens in COLOUR (bright)
// by default — place names are legible and the reveal reads clearer — while
// the rest of the site stays dark. Dark map is opt-in via the HUD toggle,
// and whichever the visitor picks is remembered for next time.
function readMapStyleMode() {
  try {
    const saved = localStorage.getItem(MAP_STYLE_KEY);
    return saved === "dark" ? "dark" : "light";
  } catch (_) {
    return "light";
  }
}
const styleForMode = (mode) => (mode === "light" ? OFM_STYLE_LIGHT : OFM_STYLE_URL);

// All 4 project sites + office for markers.
// NOTE: BlueRain sits near the Shrivardhan coast. Earlier coordinates
// ([72.9850…] then [73.0577…]) still landed on the water — the tidal
// backwater east of the Shrivardhan beach renders as sea at the map's
// zoom levels. Pushed clearly inland (east) so the pin sits on solid
// land in the Shrivardhan/Ranawali area rather than offshore.
const PROJECT_SITES = [
  {
    name:    "Nausiddhi Residency",
    lngLat:  [73.2634, 18.5576],
    loc:     "Mangaon, Raigad",
    type:    "project",
  },
  {
    name:    "BlueRain Resort Homes",
    lngLat:  [73.1000, 18.0600],
    loc:     "Ranawali, Shrivardhan",
    type:    "project",
  },
  {
    name:    "Seaview Enclaves",
    lngLat:  [73.1856, 17.7589],
    loc:     "Dapoli, Ratnagiri",
    type:    "project",
  },
  {
    name:    "Sai Shrusti",
    lngLat:  [74.4760, 19.7650],
    loc:     "Shirdi",
    type:    "project",
  },
  {
    name:    "Nausiddhi Office",
    lngLat:  OFFICE_CENTER,
    loc:     "Pimpri-Chinchwad, Pune",
    type:    "office",
  },
];

export default function MapPhase() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const { home } = useContent();
  const [phase, setLocalPhase] = useState(scrollState.phase);
  const [exiting, setExiting] = useState(false);
  const [mapStyleMode, setMapStyleMode] = useState(readMapStyleMode);
  // Target camera state derived from scroll (updated instantly, every
  // scroll event). The RAF loop below lerps the ACTUAL map camera toward
  // this target instead of jumpTo-ing straight to it — see rafRef effect.
  const targetZoomRef = useRef(ZOOM_START);
  const targetCenterRef = useRef(START_CENTER());
  // Current (rendered) camera state — what's actually been pushed to MapLibre.
  const currentZoomRef = useRef(ZOOM_START);
  const currentCenterRef = useRef(START_CENTER());
  const rafRef = useRef(null);

  useEffect(() => onPhaseChange((nextPhase) => {
    setLocalPhase((prevPhase) => {
      if (prevPhase === "MAP" && nextPhase === "PLOT") {
        setExiting(true);
        window.setTimeout(() => setExiting(false), 720);
      }
      return nextPhase;
    });
  }), []);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    loadMapLibre().then((maplibregl) => {
      if (mapRef.current || !containerRef.current) return;
      let map;
      try {
        map = new maplibregl.Map({
          container: containerRef.current,
          style: styleForMode(mapStyleMode),
          center: START_CENTER(),
          zoom: ZOOM_START,
          pitch: 30,
          bearing: isNarrowViewport() ? 0 : -5,
          interactive: false,
          attributionControl: false,
        });
      } catch (e) {
        console.warn("Map init failed:", e);
        return;
      }

      mapRef.current = map;

      map.on("load", () => {
        if (typeof window !== "undefined") {
          window.__readySignals = window.__readySignals || {};
          window.__readySignals.map = true;
        }
        try {
          PROJECT_SITES.forEach((site) => {
            const el = document.createElement("div");
            el.className = site.type === "office" ? "ml-office-pin" : "ml-project-pin";
            if (site.type === "office") {
              el.innerHTML = `<div class="ml-office-pin__pulse"></div><div class="ml-office-pin__core"></div><div class="ml-office-pin__label"><strong>${site.name}</strong><span>${site.loc}</span></div>`;
            } else {
              el.innerHTML = `<div class="ml-project-pin__pulse"></div><div class="ml-project-pin__core"></div><div class="ml-project-pin__label"><strong>${site.name}</strong><span>${site.loc}</span></div>`;
            }
            new maplibregl.Marker({ element: el, anchor: "center" })
              .setLngLat(site.lngLat)
              .addTo(map);
          });
        } catch (e) {
          console.warn("Map markers failed:", e);
        }
      });

      map.on("error", (e) => {
        if (e && e.error && e.error.name === "AbortError") return;
        console.warn("Map:", e && e.error ? e.error.message : e);
      });
    });
  }, []);

  // The map's own dark/bright skin is independent of the site theme — swap
  // tiles in place when the visitor flips the in-map toggle. MapLibre
  // Markers are DOM overlays, so the pins survive setStyle without re-adding.
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      try { map.setStyle(styleForMode(mapStyleMode)); } catch (_) {}
    }
    try { localStorage.setItem(MAP_STYLE_KEY, mapStyleMode); } catch (_) {}
  }, [mapStyleMode]);

  useEffect(() => {
    if (phase !== "MAP" || !mapRef.current) return;
    const startCenter = START_CENTER();
    try {
      mapRef.current.jumpTo({
        center: startCenter,
        zoom: ZOOM_START,
        pitch: 30,
        bearing: isNarrowViewport() ? 0 : -5,
      });
    } catch (_) {}
    // Re-entering MAP (e.g. after a JOURNEY/PLOT round-trip) must reset the
    // eased camera state too, or the RAF loop below would ease FROM the
    // stale end-of-previous-visit position instead of the reset start.
    targetZoomRef.current = ZOOM_START;
    targetCenterRef.current = startCenter;
    currentZoomRef.current = ZOOM_START;
    currentCenterRef.current = startCenter;
  }, [phase]);

  // Eases the actual MapLibre camera toward targetZoomRef/targetCenterRef
  // every frame, instead of the scroll handler jumpTo-ing straight to the
  // target on every scroll event. This is what makes fast flicks pan/zoom
  // smoothly to the destination instead of teleporting, and irons out the
  // pan/zoom easing-curve mismatch that otherwise reads as a lateral jog.
  useEffect(() => {
    if (phase !== "MAP") return undefined;

    const LERP = 0.14; // higher = snappier tracking, lower = smoother/laggier
    const tick = () => {
      const map = mapRef.current;
      if (map) {
        const tz = targetZoomRef.current;
        const [tlng, tlat] = targetCenterRef.current;
        const cz = currentZoomRef.current;
        const [clng, clat] = currentCenterRef.current;

        const nz = cz + (tz - cz) * LERP;
        const nlng = clng + (tlng - clng) * LERP;
        const nlat = clat + (tlat - clat) * LERP;

        currentZoomRef.current = nz;
        currentCenterRef.current = [nlng, nlat];

        // Skip the (relatively expensive) jumpTo once we've effectively
        // converged, so the map camera goes idle between scroll bursts
        // instead of jumpTo-ing to the same spot every frame forever.
        const settled =
          Math.abs(tz - nz) < 0.0005 &&
          Math.abs(tlng - nlng) < 0.00002 &&
          Math.abs(tlat - nlat) < 0.00002;
        if (!settled) {
          try { map.jumpTo({ zoom: nz, center: [nlng, nlat] }); } catch (_) {}
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase]);

  useEffect(() => {
    const handleScroll = (rawP) => {
      if (scrollState.phase !== "MAP") return;

      // Raw scroll fraction (rawP) is LINEAR w.r.t. actual document scroll
      // distance — a fast bottom-to-top swipe covers a large chunk of the
      // 600vh MAP track in a single Lenis tick, which used to jump rawP
      // (and the pan/zoom + handoff check below, which all read it
      // directly) by a large amount instantly. On touch devices that meant
      // one or two fast swipes could blow straight through the whole MAP
      // reveal and trigger the PLOT handoff before the user had really
      // seen it move.
      //
      // Fix: remap rawP through a curve before using it for anything —
      // fast (steep slope, small scroll covers a lot of progress) for the
      // first ~55%, then progressively slower (shallow slope, the SAME
      // swipe covers much less progress) approaching the p=1 handoff, so
      // a fast flick near the end can no longer skip past the transition.
      // Past the handoff the user is in PLOT and this curve no longer
      // applies at all — scroll there is untouched/normal.
      // FAST zone shortened (0.55 -> 0.45) and its visual payoff raised
      // (0.80 -> 0.85): the opening of the reveal now moves noticeably
      // quicker — a small early scroll covers most of the descent — while
      // the last stretch before the PLOT handoff still eases out slowly so
      // a fast flick can't skip past the transition. Net: snappier start,
      // same protected/deliberate ending.
      const FAST_END = 0.45;
      const FAST_PROGRESS = 0.85;
      let p;
      if (rawP < FAST_END) {
        // Steep ramp: reach 85% of visual progress in the first 45% of scroll.
        p = (rawP / FAST_END) * FAST_PROGRESS;
      } else {
        // Remaining 55% of raw scroll only covers the last 15% of visual
        // progress — eases out, so it settles rather than arriving
        // abruptly right at the handoff.
        const t = (rawP - FAST_END) / (1 - FAST_END);
        const slowEase = 1 - Math.pow(1 - t, 2); // easeOutQuad
        p = FAST_PROGRESS + slowEase * (1 - FAST_PROGRESS);
      }

      setMapProgress(p);

      // Pan and zoom used to lerp independently at the same linear rate.
      // With pitch applied, that makes a fixed point (the office) visibly
      // arc outward and swing back instead of tracking smoothly toward
      // centre — confirmed by sampling the office pin's on-screen position
      // through the full scroll range: it peaked ~300px off-target around
      // p=0.77 before "snapping back" by p=1. Root cause: raising zoom
      // amplifies the screen-space effect of whatever pan-gap is still
      // open, and with both progressing at the same linear rate there's
      // always a substantial gap left when zoom is highest.
      //
      // Fix: pan completes mostly by the first ~65% of scroll (eased out,
      // so it settles rather than arriving abruptly), and zoom only
      // starts ramping up after the first ~25% (eased in) — so by the
      // time zoom is significant, the office is already almost centred.
      const panT = Math.min(1, p / 0.65);
      const panEase = 1 - Math.pow(1 - panT, 3); // easeOutCubic — settles in
      const zoomT = Math.max(0, Math.min(1, (p - 0.25) / 0.75));
      const zoomEase = zoomT * zoomT * (3 - 2 * zoomT); // smoothstep — ramps in

      // Pan from the viewport-appropriate start center (portrait uses one
      // sitting nearly due-north of the office so the motion is vertical,
      // not sideways) toward the office.
      const startCenter = START_CENTER();
      const targetZoom = ZOOM_START + (ZOOM_END - ZOOM_START) * zoomEase;
      const lngLerp = startCenter[0] + (OFFICE_CENTER[0] - startCenter[0]) * panEase;
      const latLerp = startCenter[1] + (OFFICE_CENTER[1] - startCenter[1]) * panEase;
      // Only update the TARGET here — a separate RAF loop (see below) eases
      // the actual MapLibre camera toward it every frame. Previously this
      // called map.jumpTo() directly on every scroll event, which is an
      // instant camera cut: a fast flick (large scroll delta in one event,
      // amplified by Lenis's wheelMultiplier) produced a visible teleport
      // straight to the new target instead of a pan/zoom, and even normal
      // scroll speed showed pan/zoom's differing easing curves as a raw,
      // un-smoothed jog. Decoupling target updates (instant, cheap) from
      // camera rendering (eased, every frame) fixes both.
      targetZoomRef.current = targetZoom;
      targetCenterRef.current = [lngLerp, latLerp];

      if (p >= ZOOM_HANDOFF_THRESHOLD) {
        // JOURNEY (the 4-chapter landscape cinematic) has been cut — was
        // previously built, then scrapped as a product decision. Map now
        // hands off straight to PLOT (Day at Nausiddhi). SmoothScroll.jsx
        // already has a MAP->PLOT direct handler for exactly this path.
        setPhase("PLOT");
      }
    };

    const off = onScrollChange(handleScroll);
    return off;
  }, []);

  useEffect(() => {
    if (phase !== "MAP" || !mapRef.current) return;
    const t = setTimeout(() => {
      try { mapRef.current.resize(); } catch (_) {}
    }, 50);
    return () => clearTimeout(t);
  }, [phase]);

  const visible = phase === "MAP";

  return (
    <>
      <style>{`
        .map-phase-root {
          position: fixed; inset: 0; z-index: 5; pointer-events: none;
          opacity: 0; transition: opacity 720ms cubic-bezier(0.16,1,0.3,1);
          background: var(--surface-page);
          display: none;
        }
        .map-phase-root[data-visible="true"] {
          opacity: 1;
          display: block;
        }
        .map-phase-container { position: absolute; inset: 0; width: 100%; height: 100%; }

        /* The map is display-only here (interactive: false, and this whole
           root has pointer-events: none) — page scroll must drive the MAP
           phase's camera, not the map itself. MapLibre's own stylesheet
           unconditionally sets touch-action: none on .maplibregl-canvas-
           container (to block native pinch-zoom when the map IS
           interactive elsewhere on the site), which on touchscreens also
           blocks the browser's native scroll-gesture recognition for swipes
           that start over the map — this is what made MAP feel stuck/slow
           to scroll on mobile/tablet while desktop wheel scroll was
           unaffected. Force it back to pan-y so vertical swipes scroll the
           page normally. */
        .map-phase-container .maplibregl-canvas-container,
        .map-phase-container .maplibregl-canvas {
          touch-action: pan-y !important;
        }

        .map-phase-container .maplibregl-ctrl-attrib,
        .map-phase-container .maplibregl-ctrl-bottom-right,
        .map-phase-container .maplibregl-ctrl-bottom-left,
        .map-phase-container .maplibregl-ctrl-logo,
        .map-phase-container a[href*="openstreetmap.org"],
        .map-phase-container a[href*="openfreemap"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        .map-phase-vignette {
          position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(ellipse at center, transparent 50%, var(--map-vignette) 100%);
          z-index: 2;
        }
        .map-phase-hud {
          position: absolute; left: 0; right: 0; bottom: clamp(20px, 7vh, 56px);
          display: flex; justify-content: center; z-index: 3; pointer-events: none;
          padding: 0 16px;
        }
        .map-phase-hud__inner {
          padding: clamp(10px, 1.5vh, 14px) clamp(16px, 3vw, 24px);
          backdrop-filter: blur(14px);
          background: rgba(var(--ink-rgb), 0.72);
          border: 1px solid rgba(var(--gold-rgb), 0.3); border-radius: 999px;
          font-family: var(--font-display);
          color: var(--color-cream);
          letter-spacing: 0.18em;
          font-size: clamp(9px, 1.3vw, 11px);
          text-transform: uppercase;
          text-align: center;
          max-width: 100%;
        }
        .map-phase-hud__inner span { color: var(--color-gold); }

        @media (max-width: 480px) {
          .map-phase-hud__inner {
            font-size: 9px;
            letter-spacing: 0.14em;
          }
        }

        /* ── Map style toggle (independent of site theme) ─────── */
        .map-style-toggle {
          position: absolute; top: clamp(84px, 12vh, 116px); right: clamp(14px, 3vw, 32px);
          z-index: 3; display: inline-flex; align-items: center; gap: 3px;
          padding: 4px; border-radius: 999px;
          background: rgba(var(--ink-rgb), 0.8);
          border: 1px solid rgba(var(--gold-rgb), 0.28);
          backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
          box-shadow: 0 6px 22px rgba(0,0,0,0.3);
          pointer-events: auto;
        }
        .map-style-toggle__label {
          padding: 0 7px 0 8px;
          font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.22em;
          text-transform: uppercase; color: var(--color-cream-faint);
          display: inline-block;
        }
        /* Button reset - the default UA button chrome (border, padding,
           background, font metrics) is what made this look clunky. */
        .map-style-toggle__opt {
          -webkit-appearance: none; appearance: none;
          margin: 0; padding: 0; border: none; background: transparent;
          font: inherit; line-height: 0;
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px; border-radius: 50%;
          color: rgba(var(--gold-rgb), 0.6);
          cursor: pointer;
          transition: color 260ms ease, background 260ms ease;
        }
        .map-style-toggle__opt:hover { color: var(--color-gold); }
        .map-style-toggle__opt.is-active {
          color: var(--color-ink-950); background: var(--color-gold);
        }
        .map-style-toggle__opt svg { display: block; }

        @media (max-width: 480px) {
          .map-style-toggle { top: 78px; right: 12px; }
          .map-style-toggle__opt { width: 32px; height: 32px; }
          .map-style-toggle__label { font-size: 8.5px; padding: 0 5px 0 7px; }
        }

        /* ── Project pins ───────────────────────── */
        .ml-project-pin { position: relative; width: 28px; height: 28px; }
        .ml-project-pin__pulse {
          position: absolute; inset: -6px; border-radius: 50%;
          background: rgba(var(--gold-rgb), 0.35);
          animation: ml-pulse 1.8s ease-out infinite;
        }
        .ml-project-pin__core {
          position: absolute; inset: 6px; border-radius: 50%;
          background: var(--color-gold);
          box-shadow: 0 0 24px rgba(var(--gold-rgb), 0.85);
        }
        [data-theme="light"] .ml-project-pin__core {
          border: 2px solid #fff;
          box-shadow: 0 2px 10px rgba(var(--gold-rgb), 0.55);
        }
        .ml-project-pin__label {
          position: absolute; bottom: 36px; left: 50%; transform: translateX(-50%);
          white-space: nowrap; padding: 6px 14px;
          background: rgba(var(--ink-rgb), 0.9);
          border: 1px solid rgba(var(--gold-rgb), 0.5);
          font-family: var(--font-display);
          font-size: clamp(11px, 1.4vw, 13px);
          letter-spacing: 0.12em; text-transform: uppercase;
          border-radius: 999px;
        }
        .ml-project-pin__label strong {
          display: block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--color-cream);
          margin-bottom: 2px;
        }
        .ml-project-pin__label span {
          display: block;
          font-size: 9px;
          color: var(--color-cream-faint);
        }

        @keyframes ml-pulse {
          0%   { transform: scale(1);    opacity: 0.7; }
          80%  { transform: scale(2.4);  opacity: 0; }
          100% { transform: scale(2.4);  opacity: 0; }
        }

        /* ── Office marker ──────────────────────── */
        /* The container MUST carry an explicit box size. MapLibre positions
           a center-anchored marker with translate(-50%,-50%) of the
           element's OWN width - a container with no width lays out at full
           map width (~1440px), so the dot gets flung ~720px left, out into
           the Arabian Sea, while the absolutely-positioned label stays put.
           Fixed 28x28 (matching the project pins) keeps the dot on-coord. */
        .ml-office-pin { position: relative; width: 28px; height: 28px; cursor: pointer; }
        .ml-office-pin__pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: rgba(var(--gold-rgb), 0.2);
          animation: ml-pulse 2s ease-out infinite;
        }
        .ml-office-pin__core {
          position: absolute;
          inset: 7px;
          border-radius: 50%;
          background: var(--color-gold);
          border: 2px solid rgba(255,255,255,0.9);
          box-shadow: 0 2px 8px rgba(var(--gold-rgb), 0.6);
          z-index: 1;
        }
        .ml-office-pin__label {
          position: absolute;
          bottom: 36px; left: 50%;
          transform: translateX(-50%);
          background: rgba(var(--ink-rgb), 0.92);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(var(--gold-rgb), 0.4);
          padding: 6px 10px;
          white-space: nowrap;
          pointer-events: none;
          font-family: var(--font-ui);
          z-index: 2;
        }
        .ml-office-pin__label strong {
          display: block;
          font-size: 10px;
          letter-spacing: 0.12em;
          color: var(--color-gold);
          text-transform: uppercase;
          margin-bottom: 2px;
        }
        .ml-office-pin__label span {
          display: block;
          font-size: 9px;
          color: var(--color-cream-faint);
          letter-spacing: 0.06em;
        }

        /* Mobile: compact labels so five pins don't collide. */
        @media (max-width: 560px) {
          .ml-project-pin__label { padding: 4px 10px; }
          .ml-project-pin__label strong { font-size: 8.5px; letter-spacing: 0.08em; }
          .ml-project-pin__label span { display: none; }
          .ml-office-pin__label { padding: 4px 8px; }
          .ml-office-pin__label strong { font-size: 8.5px; }
          .ml-office-pin__label span { display: none; }
        }
      `}</style>
      <div className="map-phase-root" data-visible={(visible || exiting) ? "true" : "false"} style={{ opacity: visible ? 1 : 0 }}>
        <div ref={containerRef} className="map-phase-container" />
        <div className="map-phase-vignette" />
        <div
          className="map-style-toggle"
          role="radiogroup"
          aria-label="Map appearance"
        >
          <span className="map-style-toggle__label">Map</span>
          <button
            type="button"
            role="radio"
            aria-checked={mapStyleMode === "dark"}
            aria-label="Dark map"
            title="Dark map"
            className={`map-style-toggle__opt ${mapStyleMode === "dark" ? "is-active" : ""}`}
            onClick={() => setMapStyleMode("dark")}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mapStyleMode === "light"}
            aria-label="Light map"
            title="Light map"
            className={`map-style-toggle__opt ${mapStyleMode === "light" ? "is-active" : ""}`}
            onClick={() => setMapStyleMode("light")}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
            </svg>
          </button>
        </div>
        <div className="map-phase-hud">
          <div className="map-phase-hud__inner">
            <span>{home.mapHudNum}</span> &nbsp;·&nbsp; {home.mapHudText}
          </div>
        </div>
      </div>
    </>
  );
}

